import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import { ArrowLeft, Video, Webhook, RefreshCw, EyeOff, Eye, Trash2, Gauge } from "lucide-react";
import { fmtDate } from "@/pages/MyFeatured";

export default function AdminVideos() {
  const [usage, setUsage] = useState(null);
  const [hook, setHook] = useState(null);
  const load = () => Promise.all([api.get("/admin/videos/usage"), api.get("/admin/videos/webhook")]).then(([u, w]) => { setUsage(u.data); setHook(w.data); }).catch((e) => toast.error(formatApiError(e?.response?.data?.detail) || "Não foi possível carregar"));
  useEffect(() => { load(); }, []);
  return (
    <div className="page fade-up" data-testid="admin-videos-page">
      <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-zinc-500 py-3"><ArrowLeft size={14} /> Admin</Link>
      <h1 className="font-display text-2xl font-extrabold mb-3 flex items-center gap-2"><Video size={20} /> Vídeos (Cloudflare Stream)</h1>
      {usage && <UsageCard usage={usage} onChange={load} />}
      {hook && <WebhookCard hook={hook} onChange={load} />}
      <VideoList />
    </div>
  );
}

function UsageCard({ usage, onChange }) {
  const { prompt } = useConfirm();
  const pct = (a, b) => (b ? Math.min(100, Math.round((a / b) * 100)) : 0);
  const edit = async (key, label, cur) => {
    const v = await prompt({ title: label, defaultValue: String(cur), inputMode: "numeric", required: true });
    if (v == null || !Number(v)) return;
    try { await api.put("/admin/videos/limits", { [key]: Number(v) }); toast.success("Limite atualizado"); onChange(); } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Erro"); }
  };
  return (
    <section className="rounded-2xl border border-zinc-200 p-4 mb-3" data-testid="admin-videos-usage">
      <h2 className="text-sm font-bold flex items-center gap-2 mb-2"><Gauge size={14} /> Utilização e custos <span className="ml-auto text-xs font-normal text-zinc-500">ambiente: {usage.env}</span></h2>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <Stat label="Vídeos prontos" val={usage.videos_ready} />
        <Stat label="Minutos guardados" val={`${usage.stored_minutes} / ${usage.limits.stored_minutes_max}`} sub={`${pct(usage.stored_minutes, usage.limits.stored_minutes_max)}%`} onClick={() => edit("stored_minutes_max", "Máximo de minutos guardados", usage.limits.stored_minutes_max)} />
        <Stat label="Uploads hoje (plataforma)" val={`${usage.platform_usage.daily_uploads} / ${usage.limits.platform_daily_max}`} onClick={() => edit("platform_daily_max", "Máximo de uploads por dia", usage.limits.platform_daily_max)} />
        <Stat label="Minutos entregues (mês)" val={usage.analytics_error ? "indisponível" : usage.delivered_minutes_month} sub={usage.analytics_error || ""} />
        <Stat label="Custo estimado / mês" val={`US$ ${usage.estimated_month.total_usd}`} sub={`armazenamento ${usage.estimated_month.storage_usd} · entrega ${usage.estimated_month.delivery_usd}`} />
        <Stat label="Fila de eliminação" val={usage.delete_queue} />
      </div>
      {usage.recent_alerts?.length > 0 && <ul className="mt-3 text-xs text-amber-700 space-y-1" data-testid="admin-videos-alerts">{usage.recent_alerts.map((a) => <li key={a.at}>⚠ {fmtDate(a.at)} — {a.message}</li>)}</ul>}
    </section>
  );
}

function WebhookCard({ hook, onChange }) {
  const { confirm } = useConfirm();
  const [busy, setBusy] = useState(false);
  const register = async () => {
    const ok = await confirm({ title: "Apontar o webhook da Cloudflare para este ambiente?", description: `A Cloudflare passará a notificar ${hook.this_url}. O ambiente anterior continua a funcionar por polling. O novo segredo fica guardado no servidor e nunca é mostrado.`, confirmLabel: "Registar webhook" });
    if (!ok) return;
    setBusy(true);
    try { await api.post("/admin/videos/webhook/register", { confirm: true }); toast.success("Webhook registado para este ambiente"); onChange(); } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Falha ao registar"); } finally { setBusy(false); }
  };
  return (
    <section className="rounded-2xl border border-zinc-200 p-4 mb-3" data-testid="admin-videos-webhook">
      <h2 className="text-sm font-bold flex items-center gap-2 mb-2"><Webhook size={14} /> Webhook Cloudflare</h2>
      <dl className="text-xs space-y-1">
        <Row k="Aponta para este ambiente" v={hook.points_here ? "Sim ✔" : "Não — a usar polling"} testid="admin-videos-webhook-points-here" />
        <Row k="URL na Cloudflare" v={hook.cloudflare_url || "—"} />
        <Row k="URL deste ambiente" v={hook.this_url} />
        <Row k="Origem do segredo" v={{ db: "registado pela app", env: "variável de ambiente", none: "nenhum" }[hook.secret_source]} />
        <Row k="Registado em" v={hook.registered_at ? fmtDate(hook.registered_at) : "—"} />
        <Row k="Eventos recebidos" v={`${hook.events_total}${hook.last_event_at ? ` · último ${new Date(hook.last_event_at).toLocaleString("pt-PT")}` : ""}`} testid="admin-videos-webhook-events" />
      </dl>
      {!hook.points_here && (
        <button onClick={register} disabled={busy} data-testid="admin-videos-webhook-register" className="mt-3 text-xs font-semibold px-3 py-2 rounded-full bg-black text-white disabled:opacity-50 inline-flex items-center gap-1">
          <RefreshCw size={12} className={busy ? "animate-spin" : ""} /> Registar webhook neste ambiente
        </button>
      )}
    </section>
  );
}

function VideoList() {
  const { confirm, prompt } = useConfirm();
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const load = () => api.get("/admin/videos/list", { params: filter ? { state: filter } : {} }).then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);
  const act = async (v, action) => {
    if (action === "delete" && !(await confirm({ title: "Apagar vídeo definitivamente?", description: "Remove também da Cloudflare.", confirmLabel: "Apagar", tone: "danger" }))) return;
    const reason = action !== "delete" ? await prompt({ title: action === "hide" ? "Motivo para ocultar" : "Motivo para repor", defaultValue: "", placeholder: "Motivo", required: true }) : "admin";
    if (reason == null) return;
    try {
      if (action === "delete") await api.delete(`/admin/videos/${v.id}`); else await api.post(`/admin/videos/${v.id}/${action}`, { reason });
      toast.success("Feito"); load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Erro"); }
  };
  const maintenance = async () => { try { const { data } = await api.post("/admin/videos/retry-deletes"); toast.success(`Limpeza: ${data.deleted} apagados, ${data.orphans_deleted} órfãos, ${data.queued} em fila`); load(); } catch { toast.error("Erro na limpeza"); } };
  return (
    <section data-testid="admin-videos-list">
      <div className="flex items-center gap-2 mb-2 overflow-x-auto scrollbar-none">
        {["", "ready", "pendingupload", "error", "deleted"].map((s) => <button key={s} onClick={() => setFilter(s)} className="chip whitespace-nowrap" data-active={filter === s} data-testid={`admin-videos-filter-${s || "all"}`}>{s || "Todos"}</button>)}
        <button onClick={maintenance} className="chip ml-auto whitespace-nowrap" data-testid="admin-videos-maintenance"><RefreshCw size={12} className="inline mr-1" />Limpeza</button>
      </div>
      {rows.length === 0 && <p className="text-sm text-zinc-500 py-6 text-center" data-testid="admin-videos-empty">Sem vídeos.</p>}
      <ul className="space-y-2">
        {rows.map((v) => (
          <li key={v.id} className="rounded-xl border border-zinc-200 p-3 text-xs" data-testid={`admin-video-${v.id}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-zinc-500">{v.id.slice(0, 8)}</span>
              <span className="px-2 py-0.5 rounded-full bg-zinc-100 font-semibold">{v.state}</span>
              {v.hidden && <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold">oculto · {v.hidden_reason}</span>}
              {v.duration > 0 && <span>{Math.round(v.duration)}s</span>}
              <span className="text-zinc-500">{v.context?.kind || "—"} · {fmtDate(v.created_at)}</span>
              <span className="ml-auto inline-flex gap-1">
                {v.state === "ready" && !v.hidden && <button onClick={() => act(v, "hide")} className="p-1.5 rounded-full hover:bg-zinc-100" aria-label="Ocultar" data-testid={`admin-video-${v.id}-hide`}><EyeOff size={14} /></button>}
                {v.hidden && <button onClick={() => act(v, "restore")} className="p-1.5 rounded-full hover:bg-zinc-100" aria-label="Repor" data-testid={`admin-video-${v.id}-restore`}><Eye size={14} /></button>}
                {v.state !== "deleted" && <button onClick={() => act(v, "delete")} className="p-1.5 rounded-full hover:bg-red-50 text-red-600" aria-label="Apagar" data-testid={`admin-video-${v.id}-delete`}><Trash2 size={14} /></button>}
              </span>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

const Stat = ({ label, val, sub, onClick }) => (
  <button type="button" onClick={onClick} disabled={!onClick} className="text-left rounded-xl bg-zinc-50 p-3 disabled:cursor-default">
    <div className="text-zinc-500">{label}{onClick && <span className="ml-1 text-zinc-400">(editar)</span>}</div>
    <div className="font-bold text-sm">{val}</div>
    {sub && <div className="text-zinc-400 truncate">{sub}</div>}
  </button>
);
const Row = ({ k, v, testid }) => (<div className="flex gap-2"><dt className="text-zinc-500 w-44 shrink-0">{k}</dt><dd className="break-all" data-testid={testid}>{v}</dd></div>);
